import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth, User } from '../AuthContext';
import { UserBrand } from '../types/brand';
import * as brandApi from '../api/brandApi';

export interface EmulatedUser {
  id: string;
  email: string;
  username: string;
  full_name?: string;
  is_super_admin: boolean;
  brands: UserBrand[];
}

export interface EmulationContextType {
  isEmulating: boolean;
  emulatedUser: EmulatedUser | null;
  originalUser: User | null;
  effectiveUser: User | EmulatedUser | null;
  emulateUser: (targetUser: EmulatedUser) => Promise<void>;
  exitEmulation: () => void;
  canEmulate: boolean;
}

const EmulationContext = createContext<EmulationContextType | undefined>(undefined);

export const useEmulation = () => {
  const context = useContext(EmulationContext);
  if (!context) {
    throw new Error('useEmulation must be used within an EmulationProvider');
  }
  return context;
};

interface EmulationProviderProps {
  children: ReactNode;
}

export const EmulationProvider: React.FC<EmulationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [isEmulating, setIsEmulating] = useState(false);
  const [emulatedUser, setEmulatedUser] = useState<EmulatedUser | null>(null);
  const [originalUser, setOriginalUser] = useState<User | null>(null);

  // SuperAdmins can emulate
  // Reserved surface: emulation is true-superadmin only (CS excluded)
  const canEmulate = user?.is_true_super_admin || false;

  // Clear emulation if user logs out
  useEffect(() => {
    if (!user) {
      setIsEmulating(false);
      setEmulatedUser(null);
      setOriginalUser(null);
      sessionStorage.removeItem('emulated_user_id');
    }
  }, [user]);

  // Restore emulation from sessionStorage on mount
  useEffect(() => {
    const restoreEmulation = async () => {
      if (!user || !canEmulate) return;

      const storedEmulatedUserId = sessionStorage.getItem('emulated_user_id');
      if (!storedEmulatedUserId) return;

      try {
        // Fetch all users to find the emulated one
        const allUsers = await brandApi.getAllUsers();
        const targetUser = allUsers.find(u => u.id === storedEmulatedUserId);

        if (targetUser) {
          // Fetch the target user's brands
          let brands: UserBrand[] = [];
          try {
            brands = await brandApi.getUserBrandsById(targetUser.id);
            console.log(`✓ Fetched ${brands.length} brands for emulated user ${targetUser.username}`);
          } catch (err) {
            console.error('Failed to fetch brands for emulated user:', err);
          }

          setOriginalUser(user);
          setEmulatedUser({
            id: targetUser.id,
            email: targetUser.email,
            username: targetUser.username,
            full_name: targetUser.full_name,
            is_super_admin: targetUser.is_super_admin ?? false,
            brands: brands,
          });
          setIsEmulating(true);
          console.log(`✓ Restored emulation: ${targetUser.username} with ${brands.length} brands`);
        } else {
          // Clear invalid emulation
          sessionStorage.removeItem('emulated_user_id');
        }
      } catch (err) {
        console.error('Failed to restore emulation:', err);
        sessionStorage.removeItem('emulated_user_id');
      }
    };

    restoreEmulation();
  }, [user, canEmulate]);

  const emulateUser = async (targetUser: EmulatedUser) => {
    if (!user || !canEmulate) {
      throw new Error('Only SuperAdmins can emulate users');
    }

    if (targetUser.id === user.id) {
      // Trying to emulate self - just exit emulation
      exitEmulation();
      return;
    }

    // Fetch the target user's brands from backend
    let brands: UserBrand[] = [];
    try {
      brands = await brandApi.getUserBrandsById(targetUser.id);
      console.log(`✓ Fetched ${brands.length} brands for user ${targetUser.username}`);
    } catch (err) {
      console.error('Failed to fetch brands for emulated user:', err);
      // Continue with empty brands if fetch fails
    }

    setOriginalUser(user);
    setEmulatedUser({
      ...targetUser,
      brands: brands,
    });
    setIsEmulating(true);

    // Store in sessionStorage for persistence across page reloads
    sessionStorage.setItem('emulated_user_id', targetUser.id);

    console.log(`✓ Emulating user: ${targetUser.username} (${targetUser.email}) with ${brands.length} brands`);
  };

  const exitEmulation = () => {
    if (!isEmulating) return;

    setIsEmulating(false);
    setEmulatedUser(null);
    setOriginalUser(null);
    sessionStorage.removeItem('emulated_user_id');

    console.log('Exited emulation mode');
  };

  // Effective user is the emulated user if emulating, otherwise the actual user
  const effectiveUser = isEmulating ? emulatedUser : user;

  const value: EmulationContextType = {
    isEmulating,
    emulatedUser,
    originalUser,
    effectiveUser,
    emulateUser,
    exitEmulation,
    canEmulate,
  };

  return <EmulationContext.Provider value={value}>{children}</EmulationContext.Provider>;
};

export default EmulationContext;
